// src/pages/GuidePage.tsx
//
// Reddit/TikTok-style infinite-scroll feed of mixed-kind cards. Each
// card has its own renderer based on `kind`. Bottom of list triggers
// background top-up. Like/dismiss actions on every card.
//
// Backed by /users/{uid}/guide_feed/ Firestore collection. The
// `useGuideFeedBackground` runner (mounted in App) keeps the feed
// warm regardless of which tab the user is on.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Button,
  Divider,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Close,
  Favorite,
  FavoriteBorder,
  RefreshOutlined,
  ChevronRight,
  PlaceOutlined,
  EventOutlined,
  ListAltOutlined,
  HelpOutlineOutlined,
  AutoAwesomeOutlined,
  TipsAndUpdatesOutlined,
  HistoryEduOutlined,
  IosShareOutlined,
  ThumbUpAltOutlined,
  ThumbUpAlt,
  ThumbDownAltOutlined,
  ThumbDownAlt,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../services/firebase';
import { CityHighlightsRow } from '../components/collections/CityHighlightsRow';
import {
  useGuideFeed,
  useTriggerGuideFeed,
  GuideFeedCard,
  GuideCardKind,
} from '../hooks/useGuideFeed';
import { EntityDetailSheet } from '../components/EntityDetailSheet';
import { useLocation } from '../hooks/useLocation';
import type { InteractiveElement } from '../utils/interactiveContent';
import { useCardRating, useFeedbackActions } from '../hooks/useFeedback';
import { shareCard, ShareResult } from '../utils/shareCard';
import { useShareStats } from '../hooks/useShareStats';
import { GUIDE_MOODS, cardMatchesMood } from '../utils/guideMoods';
import { useT } from '../i18n/useT';

// Per-kind visual treatment
const KIND_META: Record<
  GuideCardKind,
  {
    label: string;
    color: string;
    bgColor: string;
    icon: React.ElementType;
  }
> = {
  place: {
    label: 'Place',
    color: 'rgb(124, 58, 237)',
    bgColor: 'rgba(124, 58, 237, 0.10)',
    icon: PlaceOutlined,
  },
  event: {
    label: 'Event',
    color: 'rgb(220, 38, 38)',
    bgColor: 'rgba(220, 38, 38, 0.10)',
    icon: EventOutlined,
  },
  list: {
    label: 'List',
    color: 'rgb(217, 119, 6)',
    bgColor: 'rgba(217, 119, 6, 0.10)',
    icon: ListAltOutlined,
  },
  prompt: {
    label: 'Question',
    color: 'rgb(37, 99, 235)',
    bgColor: 'rgba(37, 99, 235, 0.10)',
    icon: HelpOutlineOutlined,
  },
  suggestion: {
    label: 'Now',
    color: 'rgb(5, 150, 105)',
    bgColor: 'rgba(5, 150, 105, 0.10)',
    icon: AutoAwesomeOutlined,
  },
  tip: {
    label: 'Tip',
    color: 'rgb(75, 85, 99)',
    bgColor: 'rgba(75, 85, 99, 0.10)',
    icon: TipsAndUpdatesOutlined,
  },
  story: {
    label: 'Story',
    color: 'rgb(180, 83, 9)',
    bgColor: 'rgba(180, 83, 9, 0.10)',
    icon: HistoryEduOutlined,
  },
};

// ────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────

export const GuidePage: React.FC = () => {
  const t = useT();
  const { cards, isLoading, dismiss, setLiked } = useGuideFeed();
  const triggerTopUp = useTriggerGuideFeed();
  const navigate = useNavigate();
  const { location } = useLocation();

  // Mood filter — quick conversational filter at the top of the
  // feed. Single-select, ephemeral (resets on reload). Maps to a
  // tag-synonym match. null = no filter.
  const [activeMood, setActiveMood] = useState<string | null>(null);

  // Visible cards = not dismissed (already filtered in hook), then
  // narrowed by active mood if set. Mood matching scans the card's
  // title/body/tags/list-items — tags alone are too sparse and
  // abstract for AI-generated content.
  const visibleCards = useMemo(
    () => cards.filter((c) => cardMatchesMood(c, activeMood)),
    [cards, activeMood]
  );

  // Hand-off to chat (used by lists, prompts, suggestions)
  const openInChat = useCallback(
    (chatPrompt: string | undefined) => {
      if (!chatPrompt) return;
      sessionStorage.setItem('instantTours:autoPrompt', chatPrompt);
      navigate('/chat', { state: { autoPrompt: chatPrompt } });
    },
    [navigate]
  );

  // ── Place sheet integration ──
  // Place and event cards open the rich EntityDetailSheet — the same
  // overlay used in chat and Discover detail. It exposes pin,
  // directions, Google Place info, and "Ask in Chat" actions in one
  // consistent surface, instead of jumping straight into chat.
  const [sheetElement, setSheetElement] = useState<InteractiveElement | null>(
    null
  );

  const openPlaceSheet = useCallback((card: GuideFeedCard) => {
    // Synthesize an InteractiveElement from the card. The category is
    // 'place' for both kinds — events are at venues, the venue is the
    // place. Identifier is stable per card title so the entity-detail
    // cache (and pinned state) lines up correctly.
    const placeName = card.place_name || card.title;
    const slug = placeName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      .slice(0, 60);
    setSheetElement({
      text: placeName,
      category: 'place',
      identifier: slug,
      originalText: placeName,
    });
  }, []);
  const closePlaceSheet = useCallback(() => setSheetElement(null), []);

  // When a chat-ask happens from inside the place sheet (e.g. the user
  // taps "Ask in Chat" in EntityDetailSheet), forward to /chat the same
  // way we do for non-place cards.
  const handleSheetAskInChat = useCallback(
    (message: string) => {
      sessionStorage.setItem('instantTours:autoPrompt', message);
      navigate('/chat', { state: { autoPrompt: message } });
    },
    [navigate]
  );

  // Snackbar for share/feedback toasts
  const [toast, setToast] = useState<{
    msg: string;
    severity: 'success' | 'info' | 'warning' | 'error';
  } | null>(null);

  const handleShareResult = useCallback((result: ShareResult) => {
    if (result.method === 'native' && result.success) {
      return;
    }
    if (result.method === 'clipboard' && result.success) {
      setToast({ msg: 'Link copied to clipboard', severity: 'success' });
      return;
    }
    if (result.method === 'cancelled') {
      return;
    }
    setToast({
      msg: 'error' in result ? result.error : 'Couldn\'t share',
      severity: 'warning',
    });
  }, []);

  // ── Infinite scroll trigger ──
  // When the IntersectionObserver target near the bottom comes into
  // view, fire a top-up. The hook itself rate-limits (20s cooldown).
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            triggerTopUp('topup');
          }
        }
      },
      { rootMargin: '600px 0px' } // fire well before sentinel reaches viewport
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [triggerTopUp]);

  const handleManualRefresh = useCallback(() => {
    triggerTopUp('refresh', 8);
  }, [triggerTopUp]);

  // Backfill city highlights on first mount of the Guide. The
  // onCityChangeGenerateHighlights trigger only fires when a user
  // CROSSES into a new city — users already in a city when this
  // subsystem ships, or returning to a stale city, would otherwise
  // never see the row. The callable is idempotent: it no-ops when
  // a fresh doc exists. We fire it once per page mount.
  useEffect(() => {
    if (!auth.currentUser) return;
    const callable = httpsCallable<unknown, { ok: boolean; status?: string }>(
      functions,
      'ensureCityHighlights'
    );
    // Fire-and-forget — the row's onSnapshot listener picks up the
    // doc whenever it's written. Errors here are non-fatal and
    // shouldn't block the rest of the page from loading.
    callable({}).catch((err) => {
      console.warn('[guide] ensureCityHighlights failed:', err?.message || err);
    });
  }, []);

  // Empty state
  if (isLoading) {
    return (
      <Box sx={{ p: 2, pb: 10 }}>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Skeleton
              variant="rectangular"
              height={180}
              sx={{ borderRadius: 2 }}
            />
          </Box>
        ))}
      </Box>
    );
  }

  // Genuinely empty (no raw cards either) → first-batch loader.
  // If raw cards exist but visibleCards is 0, the active mood
  // filter is responsible — fall through to the normal render
  // below where the mood-empty hint shows instead.
  if (visibleCards.length === 0 && cards.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          pb: 10,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          mt: 4,
        }}
      >
        <Typography sx={{ fontSize: 64 }}>🗺️</Typography>
        <Typography variant="h6">{t("Gathering your feed…")}</Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 340 }}
        >
          
          {t("The first batch is generating. Hang tight — it should appear in a few seconds. If it doesn't, try a manual refresh.")}
        </Typography>
        <Button
          startIcon={<RefreshOutlined />}
          variant="outlined"
          onClick={handleManualRefresh}
          sx={{ mt: 1 }}
        >
          
          {t("Refresh")}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        // Clear the AppShell's absolute-positioned frosted header.
        // Without this, content scrolls under the blurred bar and
        // the first element (CityHighlightsRow) renders behind it.
        // 12px on top of the header height for visual breathing
        // room. Mirrors what DiscoverPage does.
        pt: 'calc(var(--app-header-h) + 12px)',
        // Clear the bottom nav too — same reason.
        pb: 'calc(var(--app-bottom-nav-h) + 16px)',
        // Slight horizontal padding so cards feel framed
        px: { xs: 1.5, sm: 2 },
      }}
    >
      {/* Personalized must-see row — circles for each AI-picked
          item, checkmarks once discovered (photo or proximity). Top
          of the Guide page so it's the first thing a user sees
          when they arrive somewhere new. Renders nothing until the
          user's city is known + highlights have generated. */}
      <CityHighlightsRow
        onAskAbout={(item) => {
          // Seed a chat conversation pre-filled with a question
          // about this highlight. The /chat route accepts a
          // `seed` query param if present.
          navigate(
            `/chat?seed=${encodeURIComponent(`Tell me about ${item.title}`)}`
          );
        }}
      />

      {/* Header strip */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
          px: 0.5,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {(visibleCards[0]?.for_city || cards[0]?.for_city)
            ? `Local feed · ${visibleCards[0]?.for_city || cards[0]?.for_city}`
            : 'Local feed'}
        </Typography>
        <Tooltip title={t("Refresh")}>
          <IconButton size="small" onClick={handleManualRefresh}>
            <RefreshOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Mood chips — quick "I'm in the mood for X" filter. Single
          select; tap an active chip to clear. Horizontally
          scrollable on narrow viewports so we don't have to wrap. */}
      <Box
        sx={{
          display: 'flex',
          gap: 0.75,
          mb: 1.5,
          overflowX: 'auto',
          pb: 0.5,
          // Hide the scrollbar for cleanliness; chips still scroll.
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {GUIDE_MOODS.map((mood) => {
          const active = activeMood === mood.key;
          return (
            <Chip
              key={mood.key}
              label={`${mood.emoji} ${mood.label}`}
              clickable
              onClick={() =>
                setActiveMood((prev) => (prev === mood.key ? null : mood.key))
              }
              variant={active ? 'filled' : 'outlined'}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: '0.78rem',
                flexShrink: 0,
                ...(active
                  ? {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': { bgcolor: 'primary.dark' },
                    }
                  : {}),
              }}
            />
          );
        })}
      </Box>

      {/* Mood-empty state — show a hint if the filter wiped the
          feed clean. */}
      {activeMood && visibleCards.length === 0 && cards.length > 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 3,
            px: 2,
            borderRadius: 2,
            bgcolor: 'action.hover',
            mb: 1.5,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            
            {t("Nothing in your feed matches \"")}
            {GUIDE_MOODS.find((m) => m.key === activeMood)?.label}{t("\" right now.")}
          </Typography>
          <Chip
            label={t("Clear filter")}
            size="small"
            onClick={() => setActiveMood(null)}
            sx={{ fontWeight: 600 }}
          />
        </Box>
      )}

      {/* Card stack */}
      <Stack spacing={1.5}>
        {visibleCards.map((card) => (
          <GuideCardItem
            key={card.id}
            card={card}
            onChat={openInChat}
            onOpenPlace={openPlaceSheet}
            onDismiss={() => dismiss(card.id)}
            onToggleLike={() => setLiked(card.id, !card.liked)}
            onShareResult={handleShareResult}
          />
        ))}
      </Stack>

      {/* Sentinel for infinite top-up */}
      <Box ref={sentinelRef} sx={{ height: 80 }} />

      {/* Place detail overlay — shared with chat & Discover */}
      <EntityDetailSheet
        open={!!sheetElement}
        onClose={closePlaceSheet}
        element={sheetElement}
        userLocation={
          location?.latitude && location?.longitude
            ? { lat: location.latitude, lng: location.longitude }
            : undefined
        }
        conversationContext="From the Guide feed"
        onAskInChat={(message) => handleSheetAskInChat(message)}
      />

      {/* Toast for share/feedback events */}
      <Snackbar
        open={!!toast}
        autoHideDuration={2400}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ mb: 8 }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            sx={{ width: '100%' }}
          >
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

// ────────────────────────────────────────────────────────────
// Single card with kind-aware renderer dispatch
// ────────────────────────────────────────────────────────────

const GuideCardItem: React.FC<{
  card: GuideFeedCard;
  onChat: (prompt: string | undefined) => void;
  onOpenPlace: (card: GuideFeedCard) => void;
  onDismiss: () => void;
  onToggleLike: () => void;
  onShareResult: (result: ShareResult) => void;
}> = ({ card, onChat, onOpenPlace, onDismiss, onToggleLike, onShareResult }) => {
  const t = useT();
  const kindMeta = KIND_META[card.kind];
  const KindIcon = kindMeta.icon;
  const rating = useCardRating(card.id);
  const { setRating } = useFeedbackActions();
  const stats = useShareStats(card.id);

  // Place and event cards open the rich place overlay (pin, directions,
  // Google Place info). Lists are non-clickable at the card level — each
  // list row is its own tap target. Tip and story are read-only.
  // Everything else (prompt, suggestion) goes straight into chat.
  const isPlaceLike = card.kind === 'place' || card.kind === 'event';
  const cardClickable =
    isPlaceLike || (card.kind !== 'list' && !!card.chat_prompt);

  const handleCardClick = () => {
    if (isPlaceLike) {
      onOpenPlace(card);
    } else if (card.chat_prompt) {
      onChat(card.chat_prompt);
    }
  };

  return (
    <Card
      variant="outlined"
      onClick={cardClickable ? handleCardClick : undefined}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative',
        cursor: cardClickable ? 'pointer' : 'default',
        transition: 'background-color 0.15s, transform 0.15s',
        '&:hover': cardClickable ? { bgcolor: 'action.hover' } : undefined,
        '&:active': cardClickable ? { bgcolor: 'action.selected' } : undefined,
      }}
    >
      {/* Photo for place/event when we resolved one */}
      {card.photo_url && (card.kind === 'place' || card.kind === 'event') && (
        <Box
          component="img"
          src={card.photo_url}
          alt={card.title}
          loading="lazy"
          sx={{
            width: '100%',
            height: 200,
            objectFit: 'cover',
            display: 'block',
          }}
        />
      )}

      <CardContent
        sx={{
          p: 2,
          '&:last-child': { pb: 2 },
        }}
      >
        {/* Kind chip + meta */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            flexWrap: 'wrap',
          }}
        >
          <Chip
            icon={<KindIcon sx={{ fontSize: 14 }} />}
            label={kindMeta.label}
            size="small"
            sx={{
              height: 22,
              bgcolor: kindMeta.bgColor,
              color: kindMeta.color,
              fontWeight: 600,
              fontSize: '0.7rem',
              '& .MuiChip-icon': { color: kindMeta.color, ml: '6px' },
              '& .MuiChip-label': { px: 1 },
            }}
          />
          {card.meta && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              {card.meta}
            </Typography>
          )}
        </Box>

        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            lineHeight: 1.25,
            mb: 0.75,
            fontSize: card.kind === 'prompt' ? 18 : 17,
          }}
        >
          {card.title}
        </Typography>

        {/* Body */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.55, whiteSpace: 'pre-wrap' }}
        >
          {card.body}
        </Typography>

        {/* List entries — clickable rows */}
        {card.kind === 'list' && card.list_items && (
          <Box sx={{ mt: 1.5 }}>
            {card.list_items.map((entry, idx) => (
              <Box
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  if (entry.chat_prompt) {
                    onChat(entry.chat_prompt);
                  } else {
                    onChat(`Tell me more about ${entry.title}.`);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.25,
                  py: 1.25,
                  cursor: 'pointer',
                  borderTop: idx === 0 ? 1 : 0,
                  borderBottom:
                    idx === (card.list_items?.length ?? 0) - 1 ? 0 : 1,
                  borderColor: 'divider',
                  transition: 'background-color 0.15s',
                  mx: -1,
                  px: 1,
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:active': { bgcolor: 'action.selected' },
                }}
              >
                <Box
                  sx={{
                    minWidth: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: kindMeta.bgColor,
                    color: kindMeta.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  {idx + 1}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, lineHeight: 1.3 }}
                  >
                    {entry.title}
                  </Typography>
                  {entry.blurb && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.25, lineHeight: 1.4, fontSize: '0.85rem' }}
                    >
                      {entry.blurb}
                    </Typography>
                  )}
                </Box>
                <ChevronRight
                  sx={{ color: 'text.disabled', mt: 0.5, flexShrink: 0 }}
                  fontSize="small"
                />
              </Box>
            ))}
          </Box>
        )}

        {/* Tags */}
        {card.tags.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1.25, flexWrap: 'wrap' }}>
            {card.tags.slice(0, 4).map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            ))}
          </Box>
        )}

        {/* Action row: like / dismiss / chat-cta for kinds that have a chat_prompt */}
        <Divider sx={{ my: 1.5 }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Tooltip title={card.liked ? 'Unlike' : 'Like'}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleLike();
              }}
              sx={{
                color: card.liked ? 'error.main' : 'text.secondary',
              }}
            >
              {card.liked ? (
                <Favorite fontSize="small" />
              ) : (
                <FavoriteBorder fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              stats?.exists && stats.viewCount > 0
                ? `${stats.uniqueViewCount} ${stats.uniqueViewCount === 1 ? 'view' : 'views'}${stats.viewCount > stats.uniqueViewCount ? ` (${stats.viewCount} total)` : ''}`
                : 'Share'
            }
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <IconButton
                size="small"
                onClick={async (e) => {
                  e.stopPropagation();
                  const result = await shareCard(card);
                  onShareResult(result);
                }}
                sx={{ color: 'text.secondary' }}
              >
                <IosShareOutlined fontSize="small" />
              </IconButton>
              {stats?.exists && stats.uniqueViewCount > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: -0.5, mr: 0.5, fontWeight: 600 }}
                >
                  {stats.uniqueViewCount}
                </Typography>
              )}
            </Box>
          </Tooltip>
          <Tooltip title={t("Dismiss")}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss();
              }}
              sx={{ color: 'text.secondary' }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          {/* Tip and story have no chat_prompt by design — pure read */}
          {card.chat_prompt &&
            card.kind !== 'list' &&
            card.kind !== 'tip' &&
            card.kind !== 'story' && (
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onChat(card.chat_prompt);
                }}
                sx={{
                  textTransform: 'none',
                  color: kindMeta.color,
                  fontWeight: 600,
                }}
              >
                
                {t("Ask AI")}
              </Button>
            )}
          {/* Thumbs — "more like this" / "less like this" feedback
              for the recommender. Thumbs-down also dismisses the
              card (signal + remove). */}
          <Tooltip title={rating === 'up' ? 'Remove vote' : 'More like this'}>
            <IconButton
              size="small"
              onClick={async (e) => {
                e.stopPropagation();
                await setRating(card, rating === 'up' ? null : 'up');
              }}
              sx={{
                color: rating === 'up' ? 'success.main' : 'text.secondary',
              }}
            >
              {rating === 'up' ? (
                <ThumbUpAlt fontSize="small" />
              ) : (
                <ThumbUpAltOutlined fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title={rating === 'down' ? 'Remove vote' : 'Less like this'}>
            <IconButton
              size="small"
              onClick={async (e) => {
                e.stopPropagation();
                const next = rating === 'down' ? null : 'down';
                await setRating(card, next);
                if (next === 'down') {
                  onDismiss();
                }
              }}
              sx={{
                color: rating === 'down' ? 'error.main' : 'text.secondary',
              }}
            >
              {rating === 'down' ? (
                <ThumbDownAlt fontSize="small" />
              ) : (
                <ThumbDownAltOutlined fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};
